import {
  BusinessKnowledge,
  CustomKnowledge,
  PaymentAndInsuranceKnowledge,
  ScheduleKnowledge,
} from '@weave/schema-gen-ts/dist/schemas/ai-settings/v1/ai_settings.pb';
import { Draft } from 'immer';
import cloneDeep from 'lodash-es/cloneDeep';
import isEqual from 'lodash-es/isEqual';
import { createProvider, withImmer } from '@frontend/store';

export interface AIRKnowledgeStoreState<T> {
  initialSettings: Record<string, T>;
  draftSettings: Record<string, T>;
  deletedDraftFields: Record<string, Array<keyof T>>;
  fieldErrors: Record<string, Record<string, boolean>>;
  lastUpdatedAt?: string;

  setInitialSettings: (settings: Record<string, T>) => void;
  setDraftSettings: (settings: Record<string, T>) => void;
  setFieldError: (locationId: string, field: string, hasError: boolean) => void;
  updateDraft: (locationId: string, data: Partial<T>) => void;
  mutateDraft: (locationId: string, mutator: (draftData: Draft<T>) => void) => void;
  updateBulkDraft: (locationIds: string[], data: Partial<T>) => void;
  mutateBulkDraft: (locationIds: string[], mutator: (draftData: Draft<T>) => void) => void;
  markFieldAsDeleted: (locationId: string, field: keyof T) => void;
  unmarkFieldAsDeleted: (locationId: string, field: keyof T) => void;
  saveDraft: () => void;
  clearDraft: () => void;
  resetStore: () => void;
}

export const createAIRKnowledgeStore = <T extends Record<string, unknown>>() => {
  return createProvider<AIRKnowledgeStoreState<T>>()(
    withImmer((set) => ({
      initialSettings: {},
      draftSettings: {},
      deletedDraftFields: {},
      fieldErrors: {},
      lastUpdatedAt: undefined,

      setInitialSettings: (initialSettings) => {
        set({ initialSettings });
      },

      setDraftSettings: (draftSettings) => {
        set({ draftSettings });
      },

      setFieldError: (locationId, field, hasError) => {
        set((state) => {
          if (!state.fieldErrors[locationId]) {
            state.fieldErrors[locationId] = {};
          }
          state.fieldErrors[locationId][field] = hasError;
        });
      },

      updateDraft: (locationId, data) => {
        set((state) => {
          const current = state.draftSettings[locationId] || ({} as T);
          state.draftSettings[locationId] = {
            ...current,
            ...data,
          };
          if (state.deletedDraftFields[locationId]) {
            state.deletedDraftFields[locationId] = state.deletedDraftFields[locationId].filter(
              (f) => !Object.keys(data).includes(f as string)
            );
          }
        });
      },

      mutateDraft: (locationId, mutator) => {
        set((state) => {
          if (!state.draftSettings[locationId]) {
            const emptyDraftSettings = {} as (typeof state.draftSettings)[string];
            state.draftSettings[locationId] = emptyDraftSettings;
          }
          mutator(state.draftSettings[locationId] as Draft<T>);
        });
      },

      updateBulkDraft: (locationIds, data) => {
        set((state) => {
          locationIds.forEach((id) => {
            const current = state.draftSettings[id] || ({} as T);
            state.draftSettings[id] = {
              ...current,
              ...data,
            };
            if (state.deletedDraftFields[id]) {
              const filteredDeletedList = state.deletedDraftFields[id].filter(
                (f) => !Object.keys(data).includes(f as string)
              );
              state.deletedDraftFields[id] = filteredDeletedList;
            }
          });
        });
      },

      mutateBulkDraft: (locationIds, mutator) => {
        set((state) => {
          locationIds.forEach((id) => {
            if (!state.draftSettings[id]) {
              const emptyDraftSettings = {} as (typeof state.draftSettings)[string];
              state.draftSettings[id] = emptyDraftSettings;
            }
            mutator(state.draftSettings[id] as Draft<T>);
          });
        });
      },

      markFieldAsDeleted: (locationId, field) => {
        set((state) => {
          state.deletedDraftFields[locationId] ??= [];
          const list = state.deletedDraftFields[locationId];
          const draftField = field as (typeof list)[number];
          if (!list.includes(draftField)) {
            list.push(draftField);
          }
          if (state.draftSettings[locationId]) {
            const draftLocationSettings = state.draftSettings[locationId] as Partial<T>;
            delete draftLocationSettings[field];
          }
          if (state.fieldErrors[locationId]) {
            delete state.fieldErrors[locationId][field as string];
          }
        });
      },

      unmarkFieldAsDeleted: (locationId, field) => {
        set((state) => {
          if (state.deletedDraftFields[locationId]) {
            const list = state.deletedDraftFields[locationId];
            const filteredList = list.filter((f) => f !== field);
            state.deletedDraftFields[locationId] = filteredList;
          }
        });
      },

      saveDraft: () => {
        set((state) => {
          state.initialSettings = cloneDeep(state.draftSettings);
          state.deletedDraftFields = {};
          state.lastUpdatedAt = new Date().toISOString();
        });
      },

      clearDraft: () => {
        set((state) => {
          state.draftSettings = cloneDeep(state.initialSettings);
          state.deletedDraftFields = {};
          state.fieldErrors = {};
        });
      },

      resetStore: () => {
        set({
          initialSettings: {},
          draftSettings: {},
          deletedDraftFields: {},
          fieldErrors: {},
          lastUpdatedAt: undefined,
        });
      },
    }))
  );
};

// Create and export the specific store for Business Knowledge Settings
export const {
  Provider: AIRBusinessKnowledgeProvider,
  useStore: useAIRBusinessKnowledgeStore,
  useNonReactiveStore: useAIRBusinessKnowledgeNonReactiveStore,
} = createAIRKnowledgeStore<BusinessKnowledge>();

// Create and export the specific store for Schedule Knowledge Settings
export const {
  Provider: AIRScheduleKnowledgeProvider,
  useStore: useAIRScheduleKnowledgeStore,
  useNonReactiveStore: useAIRScheduleKnowledgeNonReactiveStore,
} = createAIRKnowledgeStore<ScheduleKnowledge>();

// Create and export the specific store for Payment Knowledge Settings
export const {
  Provider: AIRPaymentKnowledgeProvider,
  useStore: useAIRPaymentKnowledgeStore,
  useNonReactiveStore: useAIRPaymentKnowledgeNonReactiveStore,
} = createAIRKnowledgeStore<PaymentAndInsuranceKnowledge>();

// Create and export the specific store for Custom Knowledge Settings
export const {
  Provider: AIRCustomKnowledgeProvider,
  useStore: useAIRCustomKnowledgeStore,
  useNonReactiveStore: useAIRCustomKnowledgeNonReactiveStore,
} = createAIRKnowledgeStore<CustomKnowledge>();

// Selectors for Dirty & Error calculations
export const selectLocationIsDirty =
  (locationId: string) =>
  <T>(state: AIRKnowledgeStoreState<T>) => {
    const initial = state.initialSettings[locationId];
    const draft = state.draftSettings[locationId];
    return !isEqual(initial, draft);
  };

export const selectStoreIsDirty = <T>(state: AIRKnowledgeStoreState<T>) => {
  return !isEqual(state.initialSettings, state.draftSettings);
};

export const selectLocationHasErrors =
  (locationId: string) =>
  <T>(state: AIRKnowledgeStoreState<T>) => {
    const errors = state.fieldErrors[locationId];
    if (!errors) return false;
    return Object.values(errors).some(Boolean);
  };

export const selectStoreHasErrors =
  <T>(locationIds: string[]) =>
  (state: AIRKnowledgeStoreState<T>) => {
    return locationIds.some((id) => {
      const errors = state.fieldErrors[id];
      return errors ? Object.values(errors).some(Boolean) : false;
    });
  };
